import { Component } from '@theme/component';

import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer
 * @property {HTMLElement} headerMenu
 * @property {HTMLElement} headerRowTop
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #menuDrawerHiddenWidth = null;
  #intersectionObserver = null;
  #scrollContainer = null;
  #offscreen = false;
  #lastScrollTop = 0;
  #timeout = null;
  #scrollRafId = null;

  // Desktop scroll-direction confirmation.
  #pendingWheelDirection = null;
  #wheelDirectionCount = 0;
  #lastWheelDirectionTime = 0;
  #confirmedDesktopDirection = null;

  #desktopBreakpoint = 990;
  #wheelConfirmationWindow = 350;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);

    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observe the sticky header position.
   * @param {boolean} alwaysSticky
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
      rootMargin: '0px 0px 100px 0px',
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';

        if (this.dataset.themeColor) {
          changeMetaThemeColor(this.dataset.themeColor);
        }
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer.
   * @param {boolean} hideMenu
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;

      const overflowList = this.querySelector('overflow-list');

      if (overflowList) {
        overflowList.removeAttribute('minimum-reached');
      }
    }

    setHeaderMenuStyle();
  }

  /**
   * Rebind scroll handling when crossing the desktop/mobile breakpoint.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');

    if (!stickyMode) return;

    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);

      this.#scrollContainer = getScrollEventTarget();

      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    this.#observeStickyPosition(stickyMode === 'always');

    this.#resetWheelDirectionTracking();
  };

  /**
   * Resets desktop wheel-direction confirmation.
   */
  #resetWheelDirectionTracking = () => {
    this.#pendingWheelDirection = null;
    this.#wheelDirectionCount = 0;
    this.#lastWheelDirectionTime = 0;
    this.#confirmedDesktopDirection = null;
  };

  /**
   * Requires two wheel events in the same direction within 350 ms on desktop.
   * Mobile behavior remains unchanged.
   */
  #handleWindowWheel = (event) => {
    if (window.innerWidth <= this.#desktopBreakpoint) return;

    if (this.getAttribute('sticky') !== 'scroll-up') return;

    if (event.deltaY === 0) return;

    const direction = event.deltaY < 0 ? 'up' : 'down';
    const now = performance.now();

    const withinWindow =
      now - this.#lastWheelDirectionTime <= this.#wheelConfirmationWindow;

    if (direction === this.#pendingWheelDirection && withinWindow) {
      this.#wheelDirectionCount += 1;
    } else {
      this.#pendingWheelDirection = direction;
      this.#wheelDirectionCount = 1;
    }

    this.#lastWheelDirectionTime = now;

    if (this.#wheelDirectionCount >= 2) {
      this.#confirmedDesktopDirection = direction;
    }
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');

    if (stickyMode === 'always' && !this.#offscreen) return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    // Gate desktop scroll direction until two matching wheel events occur.
    if (
      stickyMode === 'scroll-up' &&
      window.innerWidth > this.#desktopBreakpoint
    ) {
      const actualDirection = isScrollingUp ? 'up' : 'down';

      if (this.#confirmedDesktopDirection !== actualDirection) {
        this.#lastScrollTop = scrollTop;
        return;
      }
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (scrollTop <= 5) {
        this.#offscreen = true;
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'none';
      } else {
        this.#offscreen = true;
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else {
      this.#offscreen = true;
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'down';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');

    if (stickyMode) {
      if (stickyMode === 'scroll-up') {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'none';
        this.#offscreen = true;
      }

      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();

        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      window.addEventListener('wheel', this.#handleWindowWheel, { passive: true });

      scrollContainerMediaQuery.addEventListener(
        'change',
        this.#handleBreakpointChange
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();

    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);

    window.removeEventListener('wheel', this.#handleWindowWheel);

    scrollContainerMediaQuery.removeEventListener(
      'change',
      this.#handleBreakpointChange
    );

    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    this.#resetWheelDirectionTracking();

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Initial header heights are set via inline script in theme.liquid.

  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }

        return totalHeight;
      }, 0);

      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);

      document.body.style.setProperty(
        '--header-group-height',
        `${roundedHeaderGroupHeight}px`
      );
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    const children = headerGroup.children;

    for (let i = 0; i < children.length; i++) {
      const element = children[i];

      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const children = headerGroup.children;

          for (let i = 0; i < children.length; i++) {
            const element = children[i];

            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});